import { calcRSI } from "./rsiScoring.js";
import { emaLatest, ema40Weekly } from "./movingAverages.js";
import { SCANNER_ETF_CORE } from "./scannerUniverse.js";

const BROAD_ETFS = new Set(
  [...SCANNER_ETF_CORE, "SPY", "QQQ", "IWM", "DIA", "VTI", "VOO"].map((s) => s.toUpperCase())
);

export function isLongTermEtf(symbol) {
  const sym = String(symbol || "").toUpperCase();
  return BROAD_ETFS.has(sym);
}

function aggregateWeekly(dailyCloses, dailyHighs, dailyLows, dailyVolumes, weeks = 260) {
  if (!dailyCloses?.length) return { closes: [], highs: [], lows: [], volumes: [] };
  const closes = [];
  const highs = [];
  const lows = [];
  const volumes = [];
  for (let w = 0; w < weeks; w++) {
    const start = w * 5;
    if (start + 5 > dailyCloses.length) break;
    const cSlice = dailyCloses.slice(start, start + 5);
    const hSlice = dailyHighs?.slice(start, start + 5) ?? cSlice;
    const lSlice = dailyLows?.slice(start, start + 5) ?? cSlice;
    const vSlice = dailyVolumes?.slice(start, start + 5) ?? [];
    closes.push(cSlice[0]);
    highs.push(Math.max(...hSlice));
    lows.push(Math.min(...lSlice));
    volumes.push(vSlice.reduce((s, v) => s + (v || 0), 0));
  }
  return { closes, highs, lows, volumes };
}

function sma(arr, n) {
  if (!arr?.length || arr.length < n) return null;
  return arr.slice(0, n).reduce((s, v) => s + v, 0) / n;
}

function weeklyAtr(highs, lows, closes, n = 14) {
  if (!highs?.length || highs.length < n + 1) return null;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const tr = Math.max(
      highs[i] - lows[i],
      Math.abs(highs[i] - (closes[i + 1] ?? closes[i])),
      Math.abs(lows[i] - (closes[i + 1] ?? closes[i]))
    );
    sum += tr;
  }
  return sum / n;
}

function obvSlopeWeekly(closes, volumes, weeks = 20) {
  if (!closes?.length || closes.length < weeks + 1) return null;
  let obv = 0;
  const arr = [0];
  for (let i = 0; i < closes.length - 1; i++) {
    if (closes[i] > closes[i + 1]) obv += volumes[i] || 0;
    else if (closes[i] < closes[i + 1]) obv -= volumes[i] || 0;
    arr.push(obv);
  }
  const recent = arr.slice(0, 5).reduce((s, v) => s + v, 0) / 5;
  const prior = arr.slice(5, 20).reduce((s, v) => s + v, 0) / 15;
  return recent - prior;
}

function weeklyCmf(highs, lows, closes, volumes, n = 20) {
  if (!closes?.length || closes.length < n) return null;
  let mfv = 0;
  let vol = 0;
  for (let i = 0; i < n; i++) {
    const hl = highs[i] - lows[i];
    if (!hl) continue;
    mfv += ((closes[i] - lows[i]) - (highs[i] - closes[i])) / hl * (volumes[i] || 0);
    vol += volumes[i] || 0;
  }
  return vol ? mfv / vol : null;
}

function sectorSkipQuality(symbol, tickerSectorMap = {}) {
  const etf = tickerSectorMap[symbol?.toUpperCase()];
  return etf === "XLF" || etf === "XLRE";
}

/**
 * @param {object|null} fundamentals row from fundamentals_weekly
 */
export function evaluateLongTerm(result, fundamentals, weeklyBars, marketCtx = {}, opts = {}) {
  const symbol = result?.symbol ?? "";
  const isEtf = isLongTermEtf(symbol);
  const failed = [];
  const discountReasons = [];
  let qualityScore = 0;
  const qualityChecks = [];

  const f = fundamentals || {};
  const missingFundamentals = !isEtf && !f?.as_of;

  const skipRoe = sectorSkipQuality(symbol, opts.tickerSectorMap);
  const skipDebt = skipRoe;

  if (!isEtf) {
    if (f.fcf_ttm != null && f.fcf_ttm > 0) {
      qualityScore += 1;
      qualityChecks.push("FCF positive");
    } else if (f.fcf_ttm != null) failed.push("FCF negative");
    if (!skipRoe) {
      if (f.roe_ttm != null && f.roe_ttm > 12) {
        qualityScore += 1;
        qualityChecks.push("ROE > 12%");
      } else if (f.roe_ttm != null) failed.push("ROE weak");
    }
    if (!skipDebt) {
      if (f.debt_to_equity != null && f.debt_to_equity < 1.5) {
        qualityScore += 1;
        qualityChecks.push("D/E < 1.5");
      } else if (f.debt_to_equity != null) failed.push("Leverage high");
    }
    if (f.revenue_growth_3y != null && f.revenue_growth_3y > 0) {
      qualityScore += 1;
      qualityChecks.push("Revenue growth 3y");
    } else if (f.revenue_growth_3y != null) failed.push("Revenue declining");
    if (f.gross_margin != null) {
      qualityScore += 1;
      qualityChecks.push("Gross margin available");
    }
  } else {
    qualityScore = 5;
    qualityChecks.push("ETF — quality block skipped");
  }

  const qualityPass = isEtf || qualityScore >= 4;

  const daily = result?.closes;
  const weekly =
    weeklyBars?.closes?.length
      ? weeklyBars
      : aggregateWeekly(daily, result?.highs, result?.lows, result?.volumes);

  const wCloses = weekly.closes;
  const price = result?.price ?? daily?.[0];
  const ema40w = ema40Weekly(wCloses);
  const ema40wPrior = wCloses?.length > 26 ? emaLatest(wCloses.slice(26), 40) : null;

  let trendOk = false;
  let valueTrapTrend = false;
  if (ema40w != null && price != null) {
    if (price > ema40w) trendOk = true;
    else if (ema40wPrior != null && ema40w >= ema40wPrior) {
      const dist = ((ema40w - price) / ema40w) * 100;
      if (dist <= 10) trendOk = true;
    }
    if (wCloses.length >= 66 && ema40w != null && ema40wPrior != null) {
      const falling = ema40w < ema40wPrior;
      if (falling && price < ema40w) valueTrapTrend = true;
    }
  }

  const range52 = result?.range52w;
  const distHigh = range52?.distFromHigh ?? null;
  if (distHigh != null && distHigh <= -10) discountReasons.push("≥10% off 52w high");
  const ema200d = emaLatest(daily, 200);
  if (ema200d != null && price != null && Math.abs(price - ema200d) / ema200d <= 0.05) {
    discountReasons.push("Near 200-day EMA");
  }
  if (f.pe_ttm != null && f.pe_5y_median != null && f.pe_ttm < f.pe_5y_median) {
    discountReasons.push("P/E below 5y median");
  }
  const wRsi = calcRSI(wCloses, 14);
  if (wRsi != null && wRsi < 45) discountReasons.push("Weekly RSI < 45");

  const discountOk = discountReasons.length >= 2;

  const obvUp = obvSlopeWeekly(wCloses, weekly.volumes) > 0;
  const flatPrice =
    wCloses.length > 20 &&
    Math.abs((wCloses[0] - wCloses[20]) / wCloses[20]) < 0.05;
  const wCmf = weeklyCmf(weekly.highs, weekly.lows, wCloses, weekly.volumes);
  const accumulationTag =
    obvUp && flatPrice && wCmf != null && wCmf > 0 ? "Accumulation (20w OBV + CMF)" : null;

  let status = "NONE";
  if (valueTrapTrend && !qualityPass) status = "VALUE_TRAP";
  else if (discountOk && !qualityPass) status = "VALUE_TRAP";
  else if (qualityPass && trendOk && discountOk) status = "BUY_ZONE";
  else if (qualityPass || missingFundamentals) status = "WATCH";
  else if (discountOk) status = "VALUE_TRAP";
  else status = "NONE";

  if (missingFundamentals && status !== "VALUE_TRAP") status = "WATCH";

  const tranches = [];
  if (status === "BUY_ZONE" && price != null) {
    tranches.push({ id: "T1", price });
    const atrW = weeklyAtr(weekly.highs, weekly.lows, wCloses);
    const t2Candidate = ema200d != null ? Math.min(ema200d, price - (atrW ?? 0)) : price - (atrW ?? 0);
    tranches.push({ id: "T2", price: t2Candidate });
    const low52 = range52?.low ?? Math.min(...(daily?.slice(0, 252) ?? [price]));
    const high52 = range52?.high ?? Math.max(...(daily?.slice(0, 252) ?? [price]));
    const p25 = low52 + (high52 - low52) * 0.25;
    tranches.push({ id: "T3", price: p25 });
  }

  const thesisBreak =
    valueTrapTrend ||
    (qualityScore < 3 && !isEtf)
      ? "Weekly trend broken or quality score below 3"
      : null;

  return {
    status,
    qualityScore: isEtf ? null : qualityScore,
    qualityChecks,
    discountReasons,
    failed,
    tranches,
    thesisBreak,
    accumulationTag,
    fundamentalsMissing: missingFundamentals,
    peTtm: f.pe_ttm ?? null,
    pe5y: f.pe_5y_median ?? null,
    pctOff52wHigh: distHigh != null ? -distHigh : null,
  };
}

export function longTermStatusPill(longTerm) {
  const st = longTerm?.status;
  if (st === "BUY_ZONE") return { label: "🟢 LT BUY ZONE", color: "#16a34a" };
  if (st === "WATCH") return { label: "🟡 LT WATCH", color: "#ca8a04" };
  if (st === "VALUE_TRAP") return { label: "🔴 LT VALUE TRAP", color: "#ef4444" };
  return null;
}
